<template>
    <v-container fluid class="pa-0">
        <v-toolbar color="primary" density="comfortable" title="Progression Des Techniciens">
            <template #append>
                <v-btn icon="mdi-reload" :loading="isLoading" :disabled="isLoading" @click="reloadTaskLists" />
            </template>
        </v-toolbar>

        <v-progress-linear v-if="isLoading" indeterminate color="primary" class="mb-4" />

        <v-alert v-else-if="!inProgressTaskLists.length" type="info" variant="tonal">
            Aucune tournée en cours ou terminée aujourd'hui.
        </v-alert>

        <v-row v-else dense>
            <v-col v-for="taskList in inProgressTaskLists" :key="taskList.codeTournee" cols="12">
                <v-card>
                    <v-card-title class="d-flex align-center justify-space-between tasklist-card-title"
                        :style="headerStyle">
                        <span>{{ taskList.codeTournee }}</span>
                        <v-chip color="primary" variant="tonal" size="small">
                            {{ formattedTechnicians(taskList.technicians) }}
                        </v-chip>
                    </v-card-title>

                    <v-divider />

                    <v-card-text>
                        <v-stepper :model-value="activeStepValue(taskList)" alt-labels non-linear flat>
                            <v-stepper-header>
                                <template v-for="(step, index) in sortedSteps(taskList.steps)" :key="step.rank">
                                    <v-stepper-item :title="step.title" :subtitle="statusLabel(step.status)"
                                        :value="String(step.rank || '?')" :color="stepColor(step.status)"
                                        :complete="isStepCompleted(step.status)" :error="isStepInError(step.status)" />
                                    <v-divider v-if="index < taskList.steps.length - 1" />
                                </template>
                            </v-stepper-header>
                        </v-stepper>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { fetchInProgressTaskLists } from "@/utils/tasklist-api";
import { InProgressTaskList, Step } from "@/types/in-progress-task-list";
import { TaskStatus } from "@/types/enum-type";

const inProgressTaskLists = ref<InProgressTaskList[]>([]);
const isLoading = ref(false);

const color = "#88A2c8";

const headerStyle = computed(() => ({
    borderLeft: `4px solid ${color}`,
}));


async function reloadTaskLists(): Promise<void> {
    isLoading.value = true;
    try {
        inProgressTaskLists.value = await fetchInProgressTaskLists();
    } finally {
        isLoading.value = false;
    }
}

function sortedSteps(steps: Step[]): Step[] {
    return [...steps].sort((a, b) => a.rank - b.rank);
}

function isStepCompleted(status: TaskStatus): boolean {
    return status === TaskStatus.FAITE || status === TaskStatus.APPROUVE;
}

function isStepInError(status: TaskStatus): boolean {
    return status === TaskStatus.NON_FAITE || status === TaskStatus.A_REPROGRAMMER;
}

function stepColor(status: TaskStatus): string | undefined {
    return isStepCompleted(status) ? "success" : undefined;
}

function statusLabel(status: TaskStatus): string {
    const labels: Record<TaskStatus, string> = {
        [TaskStatus.A_FAIRE]: "A faire",
        [TaskStatus.ASSIGNE]: "Assigne",
        [TaskStatus.EN_COURS]: "En cours",
        [TaskStatus.FAITE]: "Faite",
        [TaskStatus.NON_FAITE]: "Non faite",
        [TaskStatus.A_REPROGRAMMER]: "A reprogrammer",
        [TaskStatus.APPROUVE]: "Approuve",
    };
    return labels[status] ?? status;
}

function activeStepValue(taskList: InProgressTaskList): string {
    const steps = sortedSteps(taskList.steps);

    const inProgressStep = steps.find((step) => step.status === TaskStatus.EN_COURS);
    if (inProgressStep) {
        return String(inProgressStep.rank);
    }

    const nextPendingStep = steps.find((step) => !isStepCompleted(step.status) && !isStepInError(step.status));
    if (nextPendingStep) {
        return String(nextPendingStep.rank);
    }

    return String(steps[steps.length - 1]?.rank ?? 1);
}

function formattedTechnicians(technicians: string[]): string {
    return technicians.length ? technicians.join(", ") : "Aucun technicien";
}

onMounted(async () => {
    await reloadTaskLists();
});
</script>

<style scoped>
.tasklist-card-title {
    background-color: rgba(136, 162, 200, 0.12);
}
</style>